import { apiClient } from '@/lib/api/client';
import { API_ENDPOINTS } from '@/lib/api/config';
import type { BulkPayload } from '@/types/bulk-action';
import type {
  ApproveDiscoveredFieldRequest,
  DiscoveredFieldStatus,
  OcrDiscoveredField,
  OcrDiscoveredFieldContainer,
  OcrDiscoveredFieldMetrics,
  OcrDiscoveredFieldContainersParams,
  OcrDiscoveredFieldFile,
  OcrDiscoveredFieldFilesParams,
  OcrDiscoveredFieldGroup,
  OcrDiscoveredFieldGroupsParams,
  OcrDiscoveredFieldsListParams,
  OcrDiscoveredFieldTemplate,
  OcrDiscoveredFieldTemplatesParams,
  RejectDiscoveredFieldRequest
} from '../types';

const { OCR_TEMPLATES } = API_ENDPOINTS;

export const discoveredFieldsApi = {
  getAll: (params?: OcrDiscoveredFieldsListParams) =>
    apiClient.getPaginated<OcrDiscoveredField>(
      OCR_TEMPLATES.DISCOVERED_FIELDS,
      params
    ),

  metrics: () =>
    apiClient.get<OcrDiscoveredFieldMetrics>(
      OCR_TEMPLATES.DISCOVERED_FIELDS_METRICS
    ),

  /** Level one of the drill-down: templates that run the free-form pass. */
  templates: (params?: OcrDiscoveredFieldTemplatesParams) =>
    apiClient.getPaginated<OcrDiscoveredFieldTemplate>(
      OCR_TEMPLATES.DISCOVERED_FIELDS_TEMPLATES,
      params
    ),

  /** Level two: containers carrying discoveries, optionally for one template. */
  containers: (params?: OcrDiscoveredFieldContainersParams) =>
    apiClient.getPaginated<OcrDiscoveredFieldContainer>(
      OCR_TEMPLATES.DISCOVERED_FIELDS_CONTAINERS,
      params
    ),

  /** The field list: one row per key, with recurrence (OMA-667). */
  fields: (params?: OcrDiscoveredFieldGroupsParams) =>
    apiClient.getPaginated<OcrDiscoveredFieldGroup>(
      OCR_TEMPLATES.DISCOVERED_FIELDS_GROUPED,
      params
    ),

  /** The containers one field turned up on, each with its pages. */
  fieldFiles: (id: number, params?: OcrDiscoveredFieldFilesParams) =>
    apiClient.getPaginated<OcrDiscoveredFieldFile>(
      OCR_TEMPLATES.DISCOVERED_FIELD_FILES(id),
      params
    ),

  /**
   * Adds the field to its template and marks its key approved.
   *
   * Labels only — the field's machine `name` is derived server-side, so the
   * same label always produces the same extraction key no matter which screen
   * created the field.
   */
  approve: (id: number, data: ApproveDiscoveredFieldRequest = {}) =>
    apiClient.post<OcrDiscoveredField>(
      OCR_TEMPLATES.DISCOVERED_FIELD_APPROVE(id),
      data
    ),

  reject: (id: number, data: RejectDiscoveredFieldRequest = {}) =>
    apiClient.post<OcrDiscoveredField>(
      OCR_TEMPLATES.DISCOVERED_FIELD_REJECT(id),
      data
    ),

  /**
   * Replace the agent's description with the supervisor's own. Sending null
   * clears it; the server flips `description_source` to `human` either way.
   */
  updateDescription: (id: number, description: string | null) =>
    apiClient.put<OcrDiscoveredField>(
      OCR_TEMPLATES.DISCOVERED_FIELD_DESCRIPTION(id),
      { description }
    ),

  /**
   * Hide the field from the review queue while it keeps being extracted
   * (OMA-669). Key-wide like every verdict.
   */
  ignore: (id: number) =>
    apiClient.post<OcrDiscoveredField>(
      OCR_TEMPLATES.DISCOVERED_FIELD_IGNORE(id)
    ),

  /** Reopen a decided key so the next run discovers the field again. */
  reset: (id: number) =>
    apiClient.post<OcrDiscoveredField>(
      OCR_TEMPLATES.DISCOVERED_FIELD_RESET(id)
    ),

  bulkDecide: (
    payload: BulkPayload,
    status: Exclude<DiscoveredFieldStatus, 'pending'>,
    rejectionReason?: string | null,
    matchThreshold?: number | null
  ) =>
    apiClient.post<{ decided: number; promoted: string[] }>(
      OCR_TEMPLATES.DISCOVERED_FIELDS_BULK_DECIDE,
      {
        ...payloadBody(payload),
        status,
        rejection_reason: rejectionReason,
        match_threshold: matchThreshold
      }
    )
};

/**
 * The backend's "ids or all+filters" body. Kept here rather than at each call
 * site so the two bulk endpoints cannot drift in what they send.
 */
function payloadBody(payload: BulkPayload) {
  return payload.mode === 'all'
    ? { all: true, filters: payload.filters }
    : { ids: payload.ids };
}
